import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    Sparkles, Users, Building2, Home as HomeIcon, Briefcase, Globe,
    ShieldCheck, KeyRound, Fingerprint, Rocket, ArrowRight, ArrowLeft,
    CheckCircle2, X, CreditCard, Coins, FileText, Upload,
} from "lucide-react";
/**
 * OnboardingWizard — 5-step first-time setup modal.
 *
 * Triggered ONCE for a brand-new tenant (no cards + no expenses in the
 * caller's workspace). Persists completion to `user_preferences.prefs.onboarding`
 * so it never nags the same user twice. Skip is always allowed.
 *
 * Steps
 *   1. Workspace type (persona)
 *   2. Region & currency (metadata only)
 *   3. Security (MFA + biometric nudge)
 *   4. First action (choose your first data point)
 *   5. Ready → jump to their chosen first action
 *
 * Design notes:
 *   - No new tables. Uses the existing /api/user/preferences store.
 *   - Persona is ALSO mirrored into localStorage so SetupWizard.js can
 *     pick it up without another round-trip.
 *   - Skip = still persists onboarding_completed=true so we don't nag.
 */

const PERSONAS = [
    { id: "personal", label: "Personal", desc: "Just me — cards, expenses, docs", icon: HomeIcon },
    { id: "family",   label: "Family",   desc: "Household finance across members", icon: Users },
    { id: "business", label: "Business", desc: "Small team or SMB workspace", icon: Briefcase },
    { id: "ca",       label: "CA Practice", desc: "Client portfolios & audits", icon: Building2 },
];

const REGIONS = [
    { id: "IN", label: "India",         currency: "INR", symbol: "₹" },
    { id: "US", label: "United States", currency: "USD", symbol: "$" },
    { id: "GB", label: "United Kingdom", currency: "GBP", symbol: "£" },
    { id: "EU", label: "European Union", currency: "EUR", symbol: "€" },
    { id: "AE", label: "United Arab Emirates", currency: "AED", symbol: "د.إ" },
    { id: "SG", label: "Singapore",     currency: "SGD", symbol: "S$" },
    { id: "AU", label: "Australia",     currency: "AUD", symbol: "A$" },
    { id: "CA", label: "Canada",        currency: "CAD", symbol: "C$" },
];

const FIRST_ACTIONS = [
    { id: "card",      icon: CreditCard, label: "Add my first credit card",     to: "/cards/new" },
    { id: "cash",      icon: Coins,      label: "Set up cash accounts",         to: "/cash-desk" },
    { id: "statement", icon: Upload,     label: "Upload a bank statement",      to: "/email-capture" },
    { id: "doc",       icon: FileText,   label: "Store an important document",  to: "/documents?add=1" },
];

export default function OnboardingWizard({ onClose, forceOpen = false }) {
    const nav = useNavigate();
    const [step, setStep] = useState(0);
    const [busy, setBusy] = useState(false);
    const [choices, setChoices] = useState({
        persona: "",
        region: "IN",
        first_action: "card",
        enable_biometric: false,
    });

    // If controlled by forceOpen, we always render. Otherwise skip completely
    // (parent decides when to mount us).
    if (!forceOpen) return null;

    const close = async (opts = {}) => {
        // Always persist "completed" so we don't nag on next login.
        // Fire-and-forget; failure is non-fatal to the UI.
        try {
            const prefs = {
                onboarding: {
                    completed: true,
                    completed_at: new Date().toISOString(),
                    persona: choices.persona || null,
                    region: choices.region || null,
                    ...(opts.skipped ? { skipped: true } : {}),
                },
            };
            await api.patch("/user/preferences", { prefs });
        } catch { /* silent */ }
        try {
            if (choices.persona) localStorage.setItem("vault_usage_type", choices.persona);
            if (choices.region) localStorage.setItem("vault_region", choices.region);
        } catch { /* noop */ }
        onClose?.();
    };

    const finish = async () => {
        setBusy(true);
        const target = FIRST_ACTIONS.find((a) => a.id === choices.first_action);
        await close();
        setBusy(false);
        if (target) nav(target.to);
    };

    const trySetupBiometric = async () => {
        try {
            const { isWebAuthnSupported, registerPasskeyForCurrentUser, friendlyDeviceLabel } =
                await import("@/lib/webauthn");
            if (!isWebAuthnSupported()) {
                toast.info("Passkeys aren't supported on this browser.");
                return;
            }
            await registerPasskeyForCurrentUser({
                deviceLabel: `Onboarding · ${friendlyDeviceLabel()}`,
            });
            toast.success("Passkey added successfully.");
            setChoices((c) => ({ ...c, enable_biometric: true }));
        } catch (e) {
            const msg = e?.message || "";
            if (!/cancel/i.test(msg)) {
                toast.error("Couldn't set up passkey. You can try again from Security → Passkeys.");
            }
        }
    };

    const canContinue = () => {
        if (step === 0) return !!choices.persona;
        if (step === 1) return !!choices.region;
        if (step === 2) return true; // security is optional
        if (step === 3) return !!choices.first_action;
        return true;
    };

    const STEPS = [
        { key: "persona",  label: "Workspace" },
        { key: "region",   label: "Region" },
        { key: "security", label: "Security" },
        { key: "action",   label: "First action" },
        { key: "ready",    label: "Ready" },
    ];

    return (
        <div
            className="fixed inset-0 z-[100] bg-stone-950/70 backdrop-blur-sm flex items-center justify-center p-4"
            data-testid="onboarding-wizard"
            role="dialog"
            aria-modal="true"
        >
            <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden max-h-[95vh] flex flex-col">
                {/* Header */}
                <div className="bg-gradient-to-br from-stone-900 to-stone-800 text-white px-6 py-5 relative">
                    <button
                        onClick={() => close({ skipped: true })}
                        className="absolute top-4 right-4 w-8 h-8 rounded-full inline-flex items-center justify-center bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors"
                        data-testid="onboarding-skip-x"
                        aria-label="Skip onboarding"
                    >
                        <X size={14} />
                    </button>
                    <div className="flex items-center gap-2 text-amber-400 text-[10px] uppercase tracking-[0.2em] font-semibold mb-1">
                        <Sparkles size={11} /> Welcome to VAULT
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-display leading-tight">
                        {step === 4 ? "You're all set." : "Let's tune VAULT for you."}
                    </h2>
                    <p className="text-[13px] text-stone-300 mt-1">
                        {step === 4
                            ? "Everything is ready. Let's add your first record."
                            : "Takes under a minute. You can change any of this later from Settings."}
                    </p>

                    {/* Step indicator */}
                    <div className="flex items-center gap-1.5 mt-4">
                        {STEPS.map((s, i) => (
                            <div
                                key={s.key}
                                className={`h-1.5 rounded-full transition-all ${
                                    i < step ? "bg-amber-400 w-6" :
                                    i === step ? "bg-white w-10" :
                                    "bg-white/20 w-6"
                                }`}
                                aria-label={s.label}
                            />
                        ))}
                    </div>
                </div>

                {/* Body */}
                <div className="p-6 overflow-y-auto flex-1" data-testid={`onboarding-step-${STEPS[step].key}`}>
                    {/* Step 0 · Persona */}
                    {step === 0 && (
                        <>
                            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-stone-500 mb-1">Step 1 of 5</div>
                            <h3 className="text-xl font-semibold text-stone-900">How will you use VAULT?</h3>
                            <p className="text-sm text-stone-500 mt-1">We&apos;ll hide modules you don&apos;t need.</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
                                {PERSONAS.map((p) => {
                                    const active = choices.persona === p.id;
                                    return (
                                        <button
                                            key={p.id}
                                            onClick={() => setChoices({ ...choices, persona: p.id })}
                                            data-testid={`onboarding-persona-${p.id}`}
                                            className={`text-left border rounded-xl p-4 transition-all ${
                                                active
                                                    ? "border-amber-500 bg-amber-50/50 ring-2 ring-amber-200"
                                                    : "border-stone-200 hover:border-stone-400 bg-white"
                                            }`}
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${active ? "bg-amber-500 text-white" : "bg-stone-100 text-stone-700"}`}>
                                                    <p.icon size={16} />
                                                </span>
                                                {active && <CheckCircle2 size={16} className="text-amber-600" />}
                                            </div>
                                            <div className="text-[15px] font-semibold text-stone-900">{p.label}</div>
                                            <div className="text-[12px] text-stone-500 mt-0.5">{p.desc}</div>
                                        </button>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {/* Step 1 · Region */}
                    {step === 1 && (
                        <>
                            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-stone-500 mb-1">Step 2 of 5</div>
                            <h3 className="text-xl font-semibold text-stone-900">Where are you based?</h3>
                            <p className="text-sm text-stone-500 mt-1">Sets your default currency, date format, and reminder timing.</p>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-5">
                                {REGIONS.map((r) => {
                                    const active = choices.region === r.id;
                                    return (
                                        <button
                                            key={r.id}
                                            onClick={() => setChoices({ ...choices, region: r.id })}
                                            data-testid={`onboarding-region-${r.id}`}
                                            className={`text-left border rounded-xl p-3 transition-all ${
                                                active
                                                    ? "border-amber-500 bg-amber-50/50 ring-2 ring-amber-200"
                                                    : "border-stone-200 hover:border-stone-400 bg-white"
                                            }`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <Globe size={13} className="text-stone-500 shrink-0" />
                                                <div className="text-[13px] font-semibold text-stone-900 truncate">{r.label}</div>
                                            </div>
                                            <div className="text-[11px] text-stone-500 mt-1">
                                                {r.symbol} <span className="uppercase tracking-wider">{r.currency}</span>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="mt-5 text-[11px] text-stone-500 bg-stone-50 border border-stone-200 rounded-lg p-3 flex items-start gap-2">
                                <ShieldCheck size={12} className="text-stone-400 mt-0.5 shrink-0" />
                                <span>Region is metadata only — we never geolocate you or share this outside your workspace.</span>
                            </div>
                        </>
                    )}

                    {/* Step 2 · Security */}
                    {step === 2 && (
                        <>
                            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-stone-500 mb-1">Step 3 of 5</div>
                            <h3 className="text-xl font-semibold text-stone-900">Lock down your VAULT.</h3>
                            <p className="text-sm text-stone-500 mt-1">Take 30 seconds now to secure your account. You&apos;ll thank yourself later.</p>
                            <div className="space-y-3 mt-5">
                                <div className="border border-stone-200 rounded-xl p-4 flex items-start gap-3 bg-white">
                                    <span className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                                        <Fingerprint size={16} className="text-blue-700" />
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[14px] font-semibold text-stone-900">Set up a Passkey — Recommended</div>
                                        <div className="text-[12px] text-stone-500 mt-0.5">Use Touch ID, Face ID or your device security for faster, safer sign-in. Your fingerprint never leaves your device.</div>
                                    </div>
                                    <button
                                        onClick={trySetupBiometric}
                                        disabled={choices.enable_biometric}
                                        className={`shrink-0 px-3 py-1.5 rounded-full text-[12px] font-semibold ${
                                            choices.enable_biometric
                                                ? "bg-emerald-100 text-emerald-800"
                                                : "bg-stone-900 text-white hover:bg-stone-700"
                                        }`}
                                        data-testid="onboarding-biometric-btn"
                                    >
                                        {choices.enable_biometric ? "Passkey added ✓" : "Set Up Passkey"}
                                    </button>
                                </div>

                                <Link
                                    to="/security"
                                    onClick={() => close()}
                                    className="border border-stone-200 rounded-xl p-4 flex items-start gap-3 bg-white hover:border-stone-400 transition-colors"
                                    data-testid="onboarding-mfa-link"
                                >
                                    <span className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                                        <KeyRound size={16} className="text-amber-700" />
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[14px] font-semibold text-stone-900">Two-factor authentication</div>
                                        <div className="text-[12px] text-stone-500 mt-0.5">Add an authenticator app (Google Authenticator, 1Password, etc.) for a second sign-in factor.</div>
                                    </div>
                                    <span className="shrink-0 text-[12px] font-semibold text-stone-700 inline-flex items-center gap-1">
                                        Set up <ArrowRight size={11} />
                                    </span>
                                </Link>

                                <div className="text-[11px] text-stone-500 bg-stone-50 border border-stone-200 rounded-lg p-3 flex items-start gap-2">
                                    <ShieldCheck size={12} className="text-stone-400 mt-0.5 shrink-0" />
                                    <span>Passwords are bcrypt-hashed. We never store CVV, OTP, or full card numbers.</span>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Step 3 · First action */}
                    {step === 3 && (
                        <>
                            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-stone-500 mb-1">Step 4 of 5</div>
                            <h3 className="text-xl font-semibold text-stone-900">Pick your first move.</h3>
                            <p className="text-sm text-stone-500 mt-1">One data point is all it takes to start seeing value.</p>
                            <div className="space-y-2 mt-5">
                                {FIRST_ACTIONS.map((a) => {
                                    const active = choices.first_action === a.id;
                                    return (
                                        <button
                                            key={a.id}
                                            onClick={() => setChoices({ ...choices, first_action: a.id })}
                                            data-testid={`onboarding-first-action-${a.id}`}
                                            className={`w-full text-left border rounded-xl p-4 flex items-center gap-3 transition-all ${
                                                active
                                                    ? "border-amber-500 bg-amber-50/50 ring-2 ring-amber-200"
                                                    : "border-stone-200 hover:border-stone-400 bg-white"
                                            }`}
                                        >
                                            <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${active ? "bg-amber-500 text-white" : "bg-stone-100 text-stone-700"}`}>
                                                <a.icon size={15} />
                                            </span>
                                            <div className="text-[14px] font-semibold text-stone-900 flex-1">{a.label}</div>
                                            {active && <CheckCircle2 size={16} className="text-amber-600 shrink-0" />}
                                        </button>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {/* Step 4 · Ready */}
                    {step === 4 && (
                        <>
                            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-stone-500 mb-1">Step 5 of 5</div>
                            <div className="text-center py-6">
                                <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto mb-4 flex items-center justify-center">
                                    <Rocket size={26} className="text-emerald-700" />
                                </div>
                                <h3 className="text-2xl font-semibold text-stone-900">Your VAULT is ready.</h3>
                                <p className="text-sm text-stone-500 mt-2 max-w-sm mx-auto">
                                    Tap <b>Get started</b> to jump into your first action.
                                    You can always come back to setup from <span className="text-stone-800 font-medium">/setup</span>.
                                </p>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4 text-[12px] text-stone-500">
                                <div className="border border-stone-200 rounded-lg p-3">
                                    <div className="font-semibold text-stone-900 mb-1 flex items-center gap-1.5"><Sparkles size={11} className="text-amber-600" /> Workspace</div>
                                    <div className="capitalize">{choices.persona || "Personal"}</div>
                                </div>
                                <div className="border border-stone-200 rounded-lg p-3">
                                    <div className="font-semibold text-stone-900 mb-1 flex items-center gap-1.5"><Globe size={11} className="text-amber-600" /> Region</div>
                                    <div>{REGIONS.find((r) => r.id === choices.region)?.label || "India"}</div>
                                </div>
                                <div className="border border-stone-200 rounded-lg p-3">
                                    <div className="font-semibold text-stone-900 mb-1 flex items-center gap-1.5"><ShieldCheck size={11} className="text-amber-600" /> Security</div>
                                    <div>{choices.enable_biometric ? "Biometric ✓" : "Password only"}</div>
                                </div>
                            </div>

                            {/* Referral prompt — friendly nudge, never blocks progress */}
                            <div
                                className="mt-5 rounded-2xl border-2 border-dashed border-amber-300 bg-gradient-to-br from-amber-50 to-amber-50/40 p-4 flex items-start gap-3"
                                data-testid="onboarding-referral-card"
                            >
                                <div className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center shrink-0">
                                    <Users size={16} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[13.5px] font-semibold text-stone-900 flex items-center gap-1.5">
                                        Invite 3 friends, get 1 month free.
                                        <span className="text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-500 text-white">Perks</span>
                                    </div>
                                    <div className="text-[12px] text-stone-600 mt-0.5">
                                        Bring anyone who cares about their money life — a family member, a friend,
                                        your CA. Every 3 sign-ups tops up your account with a free month of Pro.
                                    </div>
                                </div>
                                <Link
                                    to="/settings/team-invites"
                                    onClick={() => close()}
                                    className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-stone-900 text-white text-[12px] font-semibold hover:bg-stone-700"
                                    data-testid="onboarding-referral-cta"
                                >
                                    Invite <ArrowRight size={11} />
                                </Link>
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-stone-200 px-6 py-4 flex items-center gap-2 bg-stone-50">
                    <button
                        onClick={() => close({ skipped: true })}
                        className="text-[12px] font-medium text-stone-500 hover:text-stone-900 transition-colors"
                        data-testid="onboarding-skip"
                    >
                        Skip for now
                    </button>
                    <div className="flex-1" />
                    {step > 0 && step < 4 && (
                        <button
                            onClick={() => setStep(step - 1)}
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-stone-300 text-[13px] font-semibold text-stone-700 hover:border-stone-500"
                            data-testid="onboarding-back"
                        >
                            <ArrowLeft size={12} /> Back
                        </button>
                    )}
                    {step < 4 ? (
                        <button
                            onClick={() => setStep(step + 1)}
                            disabled={!canContinue()}
                            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-stone-900 text-white text-[13px] font-semibold hover:bg-stone-700 disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="onboarding-continue"
                        >
                            Continue <ArrowRight size={12} />
                        </button>
                    ) : (
                        <button
                            onClick={finish}
                            disabled={busy}
                            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-amber-500 text-stone-900 text-[13px] font-bold hover:bg-amber-400 disabled:opacity-60"
                            data-testid="onboarding-finish"
                        >
                            {busy ? "Getting ready…" : "Get started"} <Rocket size={12} />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
